import { useId, useState } from "react";
import { Smartphone } from "lucide-react";
import { Card } from "@/components/ds";
import { formatDubaiDateTime } from "@/lib/dubaiDate";
import {
  explainPhonePlays,
  phoneOpenMoment,
  SPLASH_PANEL_ASSUMPTION,
  whatPhonePlays,
  type PhoneSplash,
} from "@/lib/splash/whatPhonePlays";

const MAX_DAYS_AHEAD = 30;

interface WhatPhonePlaysPanelProps {
  splashes: readonly PhoneSplash[];
  /** The app the panel explains (the table filter's app). */
  appScheme: string;
  /** e.g. "Athlyn (com.athlyn.app)". */
  appLabel: string;
  now: Date;
}

/**
 * "What a phone plays" (SP-D25): a read-only explainer with a day slider (today to 30 days ahead)
 * showing which splash a phone would play, under the stated once-a-day assumption.
 */
export function WhatPhonePlaysPanel({ splashes, appScheme, appLabel, now }: WhatPhonePlaysPanelProps) {
  const [days, setDays] = useState(0);
  const sliderId = useId();
  const moment = phoneOpenMoment(now, days);
  const result = whatPhonePlays(splashes, moment, appScheme);
  const { plays, why } = explainPhonePlays(result);
  const occasion = result.plays === "splash";

  return (
    <Card className="px-4 py-3.5" data-testid="what-phone-plays">
      <h2 className="text-[14px] font-semibold text-ds-ink">What a phone plays</h2>
      <p className="mt-0.5 text-[13px] text-ds-ink2">For {appLabel}.</p>
      <p className="mt-1 text-[12.5px] text-ds-ink3">{SPLASH_PANEL_ASSUMPTION}</p>

      <label htmlFor={sliderId} className="mt-3 block text-[13px] font-semibold text-ds-ink">
        Day the phone opens the app
      </label>
      <p className="font-ds-mono text-[12.5px] text-ds-ink2" data-testid="phone-plays-day">
        {formatDubaiDateTime(moment.toISOString())}
        {days === 0 ? " (today)" : ""}
      </p>
      <input
        id={sliderId}
        type="range"
        min={0}
        max={MAX_DAYS_AHEAD}
        step={1}
        value={days}
        onChange={(event) => setDays(Number(event.target.value))}
        className="mt-1 w-full accent-[hsl(var(--ds-accent))]"
      />

      <div className="mt-3 flex items-center gap-3.5">
        <div
          aria-hidden="true"
          className={`grid aspect-[9/16] w-[54px] flex-none place-items-center rounded-lg border-4 border-ds-ink ${occasion ? "bg-ds-accent" : "bg-ds-nav"}`}
        >
          <Smartphone className="h-4 w-4 text-ds-navink" />
        </div>
        <div className="min-w-0">
          <b className="block text-[13.5px] text-ds-ink" data-testid="phone-plays-what">
            {plays}
          </b>
          <span className="text-[13px] text-ds-ink2">{why}</span>
        </div>
      </div>
    </Card>
  );
}
